import { ActionContext, Module } from 'vuex'
import { ProspectingState, RootState } from './state_models'

import { ProspectingResellingService } from '../services'

let pendingOfferingFetch: Promise<void> | null = null
let pendingSubscriptionFetch: Promise<void> | null = null
export const prospecting: Module<ProspectingState, RootState> = {
  namespaced: true,
  state: {
    locationId: undefined,
    prospectingLocationConfig: {},
    fetchingLocationConfig: false,
  },
  getters: {
    getForLocation: (state: ProspectingState) => {
      return () => state.prospectingLocationConfig
    },
    isFetchingLocationConfig: (state: ProspectingState) => {
      return state.fetchingLocationConfig
    },
    getLocationId: (state: ProspectingState) => state.locationId,
  },
  mutations: {
    set: (
      state: ProspectingState,
      payload: {
        locationId?: string
        prospectingLocationConfig?: { [key: string]: any }
      }
    ) => {
      if (payload.locationId !== undefined) state.locationId = payload.locationId
      if (payload.prospectingLocationConfig !== undefined)
        state.prospectingLocationConfig = {
          ...state.prospectingLocationConfig,
          ...payload.prospectingLocationConfig,
        }
    },

    resetFetchingLocationConfig: (state: ProspectingState) => {
      state.fetchingLocationConfig = false
    },

    setFetchingLocationConfig: (state: ProspectingState, value: boolean) => {
      state.fetchingLocationConfig = value
    },
  },
  actions: {
    async syncAllProspectingConfig(
      context: ActionContext<ProspectingState, RootState>,
      payload: { locationId: string }
    ) {
      const { locationId } = payload

      if (!locationId) return

      // Reset state before fetching new location data
      context.commit('set', {
        prospectingLocationConfig: {},
      })
      context.commit('setFetchingLocationConfig', true)

      try {
        await Promise.all([
          context.dispatch('syncOffering', locationId),
          context.dispatch('syncSubscription', locationId),
        ])
      } catch {
        // silent catch
      } finally {
        context.commit('setFetchingLocationConfig', false)
      }
    },
    /**
     * Sync offeringEnabled + optInEnabled for a location
     */
    async syncOffering(context: ActionContext<ProspectingState, RootState>, locationId: string) {
      if (!locationId) return

      // If already fetching, return the same pending promise
      if (pendingOfferingFetch) {
        return pendingOfferingFetch
      }

      pendingOfferingFetch = (async () => {
        try {
          const response = await ProspectingResellingService.getLocationResellingConfig(locationId)
          context.commit('set', {
            prospectingLocationConfig: {
              offeringEnabled: response?.offeringEnabled,
              optInEnabled: response?.companyCustomFields?.optInEnabled || false,
            },
          })
        } catch {
          // silent catch
        } finally {
          pendingOfferingFetch = null
        }
      })()

      return pendingOfferingFetch
    },

    /**
     * Sync subscription for location
     */
    async syncSubscription(context: ActionContext<ProspectingState, RootState>, locationId: string) {
      if (!locationId) return

      // If already fetching, return the same pending promise
      if (pendingSubscriptionFetch) {
        return pendingSubscriptionFetch
      }

      pendingSubscriptionFetch = (async () => {
        try {
          const response = await ProspectingResellingService.getLocationSubscriptions(locationId)
          const subscribed = response?.[0]?.paymentStatus?.includes('COMPLETE') || false

          context.commit('set', {
            prospectingLocationConfig: {
              subscribed: subscribed,
            },
          })
        } catch {
          // silent catch
        } finally {
          pendingSubscriptionFetch = null
        }
      })()

      return pendingSubscriptionFetch
    },

    resetFetchingLocationConfig(context: ActionContext<ProspectingState, RootState>) {
      try {
        context.commit('resetFetchingLocationConfig')
      } catch {
        return
      }
    },
  },
}
